const MAX_FRAME_EDGE = 1280;
const MAX_FRAME_PIXELS = 1280 * 720;

/** Release an owned snapshot's pixel backing without waiting for browser GC. */
export function releaseCanvasFrame(canvas) {
  if (!canvas) return;
  try {
    if (canvas.width !== 0) canvas.width = 0;
    if (canvas.height !== 0) canvas.height = 0;
  } catch (_) { /* Cleanup must not interrupt navigation. */ }
}

/**
 * Copy a decoded video or canvas into a bounded temporary display frame.
 * The caller owns the returned canvas and must release it when finished.
 * Cross-origin drawing is allowed; snapshots are never exported or read back.
 */
export function captureVideoFrame(source, existingCanvas = null) {
  if (!source) return null;
  const isVideo = typeof source.videoWidth === "number";
  if (isVideo && source.readyState < 2) return null;
  const sourceWidth = isVideo ? source.videoWidth : source.width;
  const sourceHeight = isVideo ? source.videoHeight : source.height;
  if (!Number.isFinite(sourceWidth) || !Number.isFinite(sourceHeight)
    || sourceWidth <= 0 || sourceHeight <= 0) return null;

  const scale = Math.min(1, MAX_FRAME_EDGE / sourceWidth, MAX_FRAME_EDGE / sourceHeight,
    Math.sqrt(MAX_FRAME_PIXELS / (sourceWidth * sourceHeight)));
  const width = Math.max(1, Math.floor(sourceWidth * scale));
  const height = Math.max(1, Math.floor(sourceHeight * scale));
  let frame = existingCanvas === source ? null : existingCanvas;
  try {
    frame = frame || document.createElement("canvas");
    // Resetting even an unchanged dimension clears/reallocates its backing.
    if (frame.width !== width) frame.width = width;
    if (frame.height !== height) frame.height = height;
    const context = frame.getContext("2d");
    if (!context) throw new Error("Frame canvas unavailable");
    context.drawImage(source, 0, 0, width, height);
    return frame;
  } catch (_) {
    releaseCanvasFrame(frame);
    return null;
  }
}
